import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Loader, Center, Stack, Text } from '@mantine/core';
import { CUSTOMER_ROUTES, ADMIN_ROUTES, NONPROFIT_ROUTES, MARKETING_ROUTES } from '../../routing/routes';

/**
 * HomeRedirect Component
 *
 * Sprint 041 Session 1: Auth-aware wrapper for the `/` route.
 *
 * Behavior:
 * - If authenticated + admin → redirect to /admin
 * - If authenticated + customer → redirect to /mentions
 * - If not authenticated → render <Outlet /> (marketing pages via PublicLayout)
 * - Shows loading state while auth is initializing to prevent flash-of-wrong-content
 *
 * Only redirects when user is on the home route `/`.
 * Other marketing routes (e.g. /benefits) pass through without redirect.
 */
export const HomeRedirect: React.FC = (): React.ReactElement => {
  const { isAuthenticated, isAdmin, isCustomer, isNonProfitUser, isLoading } = useAuth();
  const location = useLocation();

  // Only apply auth redirect logic on the home route
  const isHomeRoute = location.pathname === MARKETING_ROUTES.HOME;

  // Show loading state while auth is initializing (only on home route)
  if (isHomeRoute && isLoading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Loading...
          </Text>
        </Stack>
      </Center>
    );
  }

  // Redirect authenticated users away from home page
  if (isHomeRoute && isAuthenticated) {
    if (isAdmin) {
      return <Navigate to={ADMIN_ROUTES.DASHBOARD} replace />;
    }
    if (isCustomer) {
      return <Navigate to={CUSTOMER_ROUTES.MENTIONS} replace />;
    }
    if (isNonProfitUser) {
      return <Navigate to={NONPROFIT_ROUTES.DASHBOARD} replace />;
    }
  }

  // Not authenticated or not on home route - render marketing pages
  return <Outlet />;
};
